import { useEffect, useRef, useState } from "react";
import { fetchTurbineList } from "../../api/channelSelection";
import LangDateInput from "../LangDateInput";
import type { ChannelInfo, TurbineInfo } from "../../types/dialog-types";
import { dateTimeToTigerSeconds, intervalToSeconds, validateEndAfterStart } from "../../utils/tigerTime";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";
import { getTigerResource, tigerFormat } from "../../utils/tigerResource";
import { PickerKey, pickerSavePath, rememberPickerDir } from "../../utils/pickerFolder";
import { downloadReport } from "../../utils/downloadReport";
import { promptReportSave } from "../../utils/reportSavePrompt";
import { EXPORT_DATA_FORMATS, toTauriFilters } from "../../utils/reportFormats";
import { useProgressPoll } from "../../utils/useProgressPoll";
import { GraphChannelSelectionDialog } from "./GraphChannelSelectionDialog";

/** Latest export progress polled from the webserver while a web export is in
 *  flight (mirrors the `export-progress` Tauri payload the desktop dialog uses). */
interface WebExportProgress {
  percent: number;
}

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Binary export-type radios → the integer the backend expects. Labels are
 *  resource IDs resolved at render time (so language overrides apply). */
const EXPORT_TYPES: { value: number; labelId: string }[] = [
  { value: 0, labelId: "TIGER_CHANNEL_CUR" },
  { value: 1, labelId: "TIGER_GRAPH_MAX" },
  { value: 2, labelId: "TIGER_GRAPH_MIN" },
  { value: 3, labelId: "TIGER_EXPORT_AVERAGE" },
];

/** A channel queued for export (the C++ `export_tiger_channel_list`). */
interface ExportChannel {
  tag: string;
  description: string;
  channel_type: string;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** "data" = binary export (extra event/type/logic options); "trend" = trend export. */
  variant?: "data" | "trend";
}

/**
 * Port of the C++ `binary_export_dialog_shell_c` / `trend_export_dialog_shell_c`.
 * Collects a turbine, time range, sample interval and channel list, then sends a
 * `REMOTE_DATA_SERVER_EXPORT_BINARY` / `_TREND` request (via the `export_data` /
 * `export_trend` commands) after the user picks a save path. The data server
 * writes the tab-separated file and drives the progress dialog.
 *
 * Styled to match `GraphChannelSelectionDialog` (header / scrolling body of
 * cards / footer).
 */
export function ExportDialog({ open, onClose, variant = "data" }: ExportDialogProps) {
  const isTrend = variant === "trend";

  const [turbines, setTurbines] = useState<TurbineInfo[]>([]);
  const [turbine, setTurbine] = useState("");

  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [sampleInterval, setSampleInterval] = useState(isTrend ? "00:04:00" : "00:00:01");

  // Binary-only options.
  const [eventModels, setEventModels] = useState<Array<[string, string]>>([]);
  const [model, setModel] = useState(""); // "" => TIME
  const [exportType, setExportType] = useState(0);
  const [logic, setLogic] = useState("");

  // Export channel list (the queued-for-export tags).
  const [channels, setChannels] = useState<ExportChannel[]>([]);
  const [selectedChannelIndex, setSelectedChannelIndex] = useState(-1);

  // Graph-style channel-selection picker (opened by the "Add…" button).
  const [showChannelSelection, setShowChannelSelection] = useState(false);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Web: the export POST blocks for the whole round-trip, so poll the server's
  // progress in parallel to drive a determinate bar (desktop uses the
  // `export-progress` event + the standalone ExportProgressDialog instead).
  const webProgress = useProgressPoll<WebExportProgress>(
    !isTauri() && busy && turbine
      ? `/api/get_export_progress?config_name=${encodeURIComponent(turbine)}`
      : null,
  );

  // Whether the "today" default range has been applied yet. The dialog stays
  // mounted between opens, so applying defaults only once lets the start/end
  // time the user last entered persist between calls.
  const defaultsAppliedRef = useRef(false);

  // Load turbines when the dialog opens; reset transient state. The time range
  // defaults to "today, 00:00:00 → 23:59:59" only on the first open — after
  // that the user's last-entered range is remembered.
  useEffect(() => {
    if (!open) return;
    setError("");
    setShowChannelSelection(false);
    if (!defaultsAppliedRef.current) {
      defaultsAppliedRef.current = true;
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      setStartDate(today);
      setEndDate(today);
      setStartTime("00:00:00");
      setEndTime("23:59:59");
    }
    fetchTurbineList().then((list) => {
      setTurbines(list);
      setTurbine((cur) => cur || list[0]?.config_name || "");
    });
  }, [open]);

  // Fetch event models for the chosen turbine (binary variant only).
  useEffect(() => {
    if (!open || isTrend || !turbine) {
      setEventModels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (!isTauri()) return;
        const { invoke } = await import("@tauri-apps/api/core");
        const list = await invoke<Array<[string, string]>>("get_event_models", {
          configName: turbine,
        });
        if (!cancelled) setEventModels(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setEventModels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isTrend, turbine]);

  // A channel was picked in the graph-style selection dialog. Queue it for
  // export (deduping by tag) and, since the binary export targets a single
  // turbine, keep the export turbine in step with the channel's config.
  const handleApplyChannel = (data: unknown) => {
    const { channel, config_name: cfg } = data as {
      channel?: ChannelInfo;
      config_name?: string;
    };
    if (!channel) return;
    if (cfg && cfg !== turbine) setTurbine(cfg);
    setChannels((prev) =>
      prev.some((c) => c.tag === channel.tag)
        ? prev
        : [...prev, { tag: channel.tag, description: channel.description, channel_type: channel.channel_type }],
    );
  };

  const handleRemove = () => {
    if (selectedChannelIndex < 0) return;
    setChannels((prev) => prev.filter((_, i) => i !== selectedChannelIndex));
    setSelectedChannelIndex(-1);
  };

  const handleClear = () => {
    setChannels([]);
    setSelectedChannelIndex(-1);
  };

  const handleOk = async () => {
    setError("");
    if (!turbine) {
      setError(getTigerResource("TIGER_EXPORT_SELECT_TURBINE"));
      return;
    }
    const start = dateTimeToTigerSeconds(startDate, startTime);
    const end = dateTimeToTigerSeconds(endDate, endTime);
    if (start == null || end == null) {
      setError(getTigerResource("TIGER_EXPORT_INVALID_TIME"));
      return;
    }
    const rangeErr = validateEndAfterStart(start, end);
    if (rangeErr) {
      setError(rangeErr);
      return;
    }
    const samplerate = intervalToSeconds(sampleInterval);
    if (samplerate <= 0) {
      setError(getTigerResource("TIGER_EXPORT_SAMPLE_POSITIVE"));
      return;
    }
    if (channels.length === 0) {
      setError(getTigerResource("TIGER_EXPORT_NO_CHANNELS"));
      return;
    }

    const tags = channels.map((c) => c.tag);
    const defaultName = `${turbine}_tiger_export.txt`;

    if (!isTauri()) {
      // Web: no native save dialog and no progress events. Ask for the name +
      // format, then POST the export request; the webserver runs the
      // data-server round-trip into a temp file and streams it back, which the
      // browser saves to Downloads. The fetch stays pending (dialog shows
      // "Exporting…") until it completes.
      const filename = await promptReportSave({
        defaultName,
        formats: EXPORT_DATA_FORMATS,
        rememberKey: PickerKey.exportData,
        titleId: "TIGER_EXPORT_SAVE_TITLE",
      });
      if (!filename) return; // cancelled
      setBusy(true);
      try {
        const body = isTrend
          ? { config_name: turbine, start_time: start, end_time: end, tags, filename }
          : {
              config_name: turbine,
              start_time: start,
              end_time: end,
              samplerate,
              tags,
              filename,
              model_name: model || "TIME",
              export_type: exportType,
              logic,
            };
        await downloadReport(isTrend ? "/api/export_trend" : "/api/export_data", filename, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        onClose();
      } catch (e) {
        setError(tigerFormat(getTigerResource("TIGER_EXPORT_FAILED"), e instanceof Error ? e.message : String(e)));
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      const { save } = await import("@tauri-apps/plugin-dialog");
      const path = await save({
        title: getTigerResource("TIGER_EXPORT_SAVE_TITLE"),
        defaultPath: await pickerSavePath(PickerKey.exportData, defaultName),
        filters: [
          ...toTauriFilters(EXPORT_DATA_FORMATS),
          // Wildcard: a desktop-only filter (nothing to choose in the browser).
          { name: getTigerResource("TIGER_EXPORT_FILTER_ALL"), extensions: ["*"] },
        ],
      });
      if (!path) {
        setBusy(false);
        return; // user cancelled the save dialog
      }
      rememberPickerDir(PickerKey.exportData, path);

      const { invoke } = await import("@tauri-apps/api/core");
      if (isTrend) {
        // No samplerate: the backend uses the turbine's online trend sample rate.
        await invoke("export_trend", {
          configName: turbine,
          startTime: start,
          endTime: end,
          tags,
          filename: path,
        });
      } else {
        await invoke("export_data", {
          configName: turbine,
          startTime: start,
          endTime: end,
          samplerate,
          tags,
          filename: path,
          modelName: model || "TIME",
          exportType,
          logic,
        });
      }
      onClose();
    } catch (e) {
      setError(tigerFormat(getTigerResource("TIGER_EXPORT_FAILED"), String(e)));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const title = isTrend
    ? getTigerResource("TIGER_EXPORT_TREND_TITLE")
    : getTigerResource("TIGER_EXPORT_BIN_TITLE");

  return (
    <>
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.dialog}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose} disabled={busy}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Time selection */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_EXPORT_TIME")}</span>
            <div style={styles.card}>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_TURBINE")}</label>
                  <select style={styles.select} value={turbine} onChange={(e) => setTurbine(e.target.value)}>
                    {turbines.length === 0 && <option value="">{getTigerResource("TIGER_EXPORT_SELECT_TURBINE_OPTION")}</option>}
                    {turbines.map((t) => (
                      <option key={t.config_name} value={t.config_name}>
                        {t.turbine_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_START_TIME")}</label>
                  <input style={styles.input} type="time" step={1} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_START_DATE")}</label>
                  <LangDateInput style={styles.input} value={startDate} onChange={setStartDate} />
                </div>
              </div>
              <div style={styles.fieldGroup}>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_END_TIME")}</label>
                  <input style={styles.input} type="time" step={1} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </div>
                <div style={styles.field}>
                  <label style={styles.fieldLabel}>{getTigerResource("TIGER_RES_END_DATE")}</label>
                  <LangDateInput style={styles.input} value={endDate} onChange={setEndDate} />
                </div>
              </div>
            </div>
          </div>

          {/* Binary options */}
          {!isTrend && (
            <div style={styles.section}>
              <span style={styles.sectionLabel}>{getTigerResource("TIGER_EXPORT_BINARY")}</span>
              <div style={styles.card}>
                <div style={styles.fieldGroup}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel}>{getTigerResource("TIGER_CHANNEL_EVENT")}</label>
                    <select style={styles.select} value={model} onChange={(e) => setModel(e.target.value)}>
                      <option value="">{getTigerResource("TIGER_GRAPH_TIME")}</option>
                      {eventModels.map(([id, name]) => (
                        <option key={id} value={name || id}>
                          {name || id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.fieldNarrow}>
                    <label style={styles.fieldLabel}>{getTigerResource("TIGER_EXPORT_SAMPLE")}</label>
                    <input style={styles.input} type="time" step={1} value={sampleInterval} onChange={(e) => setSampleInterval(e.target.value)} />
                  </div>
                </div>
                <div style={styles.fieldRow}>
                  {EXPORT_TYPES.map((et) => (
                    <label key={et.value} style={styles.radioLabel}>
                      <input
                        style={styles.radio}
                        type="radio"
                        name="exportType"
                        checked={exportType === et.value}
                        onChange={() => setExportType(et.value)}
                      />
                      {getTigerResource(et.labelId)}
                    </label>
                  ))}
                </div>
                <div style={styles.fieldRow}>
                  <label style={styles.label}>{getTigerResource("TIGER_EXPORT_LOGIC")}</label>
                  <input style={styles.input} type="text" value={logic} onChange={(e) => setLogic(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* Channel selection */}
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_EXPORT_CHANNEL")}</span>
            <div style={styles.btnRow}>
              <button className="btn-acrylic-secondary" onClick={() => setShowChannelSelection(true)} disabled={!turbine}>
                {getTigerResource("TIGER_EXPORT_ADD")}
              </button>
              <button className="btn-acrylic-secondary" onClick={handleRemove} disabled={selectedChannelIndex < 0}>
                {getTigerResource("TIGER_RES_REMOVE")}
              </button>
              <button className="btn-acrylic-secondary" onClick={handleClear} disabled={channels.length === 0}>
                {getTigerResource("TIGER_RES_CLEAR")}
              </button>
            </div>

            <div style={styles.tableWrap}>
              <div
                className="list-control"
                style={{ maxHeight: 170, overflow: "auto" }}
                tabIndex={0}
                onKeyDown={(e) => {
                  // Delete key removes the selected tag (same as the Remove button).
                  if ((e.key === "Delete" || e.key === "Backspace") && selectedChannelIndex >= 0) {
                    e.preventDefault();
                    handleRemove();
                  }
                }}
              >
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 170 }}>{getTigerResource("TIGER_CHANNEL_TAG")}</th>
                      <th>{getTigerResource("TIGER_CHANNEL_DESCR")}</th>
                      <th style={{ width: 90 }}>{getTigerResource("TIGER_GRAPH_TYPE")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map((ch, idx) => (
                      <tr
                        key={idx}
                        className={selectedChannelIndex === idx ? "selected" : ""}
                        onClick={() => setSelectedChannelIndex(idx)}
                      >
                        <td>{ch.tag}</td>
                        <td>{ch.description}</td>
                        <td>{ch.channel_type}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          {/* Web export progress: a determinate bar while the export round-trip
              is pending (indeterminate until the first progress poll arrives). */}
          {!isTauri() && busy && (
            <div style={styles.progressRow}>
              <div style={styles.barOuter}>
                {webProgress ? (
                  <>
                    <div
                      style={{
                        ...styles.barInner,
                        width: `${Math.max(0, Math.min(100, webProgress.percent))}%`,
                      }}
                    />
                    <div style={styles.barLabel}>
                      {Math.max(0, Math.min(100, webProgress.percent)).toFixed(0)}%
                    </div>
                  </>
                ) : (
                  <div className="tiger-indeterminate-bar" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onClose} disabled={busy}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
          <button className="btn-acrylic-primary" onClick={handleOk} disabled={busy}>
            {busy ? getTigerResource("TIGER_EXPORT_EXPORTING") : getTigerResource("TIGER_RES_OK")}
          </button>
        </div>
      </div>
    </div>

    <GraphChannelSelectionDialog
      open={showChannelSelection}
      onClose={() => setShowChannelSelection(false)}
      onApply={handleApplyChannel}
      mode={isTrend ? "trend" : "live"}
      defaultConfig={turbine}
      hideTurbineState
      title={isTrend ? getTigerResource("TIGER_GRAPH_CHANNEL_TREND_TITLE") : getTigerResource("TIGER_GRAPH_CHANNEL_SELECT_TITLE")}
    />
    </>
  );
}

const styles = {
  ...acrylicStyles,
  dialog: acrylicDialogSurface({ width: 600, height: 680, minWidth: 480, minHeight: 560 }),
  error: {
    color: "#b91c1c",
    fontSize: 12,
  },
  progressRow: {
    marginTop: 8,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden" as const,
  },
  barInner: {
    height: "100%",
    background: "var(--ui-btn-primary-bg)",
    transition: "width 0.2s ease",
  },
  barLabel: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#4b5260",
  },
} as const;
